import { useEffect, useRef, useState } from 'react';

/**
 * Hook reativo para o status de conexao do navegador.
 *
 * Combina `navigator.onLine` (snapshot) com os eventos `online`/`offline`
 * do `window`. Aplica debounce em transicoes para `offline` (default 1000ms)
 * para evitar flicker em quedas espurias — alguns sistemas (Windows + WSL,
 * tablets em troca de rede) disparam `offline` imediatamente seguido de
 * `online` quando trocam de adaptador.
 *
 * Transicoes para `online` sao aplicadas imediatamente (sem debounce) — voltar
 * a estar online e sempre uma boa noticia que o usuario pode ver na hora.
 *
 * Equivalente do mobile: wrapper sobre `@react-native-community/netinfo` em
 * `src/shared/hooks/use-online-status.ts` do `grana-money-app`.
 *
 * Em SSR (sem `navigator`) retorna `true` por default — assume online.
 */
export interface UseOnlineStatusOptions {
  /** Debounce em ms para transicoes para offline. Default 1000. */
  offlineDebounceMs?: number;
}

export function useOnlineStatus(options: UseOnlineStatusOptions = {}): boolean {
  const { offlineDebounceMs = 1000 } = options;
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const offlineTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const clearOfflineTimer = () => {
      if (offlineTimer.current !== null) {
        clearTimeout(offlineTimer.current);
        offlineTimer.current = null;
      }
    };

    const onOnline = () => {
      clearOfflineTimer();
      setOnline(true);
    };

    const onOffline = () => {
      clearOfflineTimer();
      offlineTimer.current = setTimeout(() => {
        setOnline(false);
        offlineTimer.current = null;
      }, offlineDebounceMs);
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearOfflineTimer();
    };
  }, [offlineDebounceMs]);

  return online;
}
